import { baseApi } from '../api/baseApi';
import type {
    OrganizationCreate,
    OrganizationList,
    OrganizationUser,
    OrganizationDetailsResponse,
    UpdateOrganizationRequest,
    UpdateOrganizationResponse,
    SuccessResponse,
    UserInviteRequest,
    UpdateUserRoleRequest,
    UpdateUserRoleResponse,
    RemoveUserRequest,
    RemoveUserResponse,
    ResendInviteRequest,
    ResendInviteResponse,
} from '../api/types';

// Extend the base API with organization-specific endpoints
export const organizationApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        // GET /organizations/list/ - List organizations user has access to
        getOrganizations: builder.query<OrganizationList[], void>({
            query: () => ({
                url: '/organizations/list/',
                method: 'GET',
            }),
            providesTags: (result) =>
                result
                    ? [
                        ...result.map(({ id }) => ({ type: 'Organization' as const, id })),
                        { type: 'Organization', id: 'LIST' },
                    ]
                    : [{ type: 'Organization', id: 'LIST' }],
        }),

        // POST /organizations/ - Create a new organization
        createOrganization: builder.mutation<OrganizationList, OrganizationCreate>({
            query: (data) => ({
                url: '/organizations/',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: [{ type: 'Organization', id: 'LIST' }],
        }),

        // GET /organization/details/ - Get organization details including license, API usage, and seats
        getOrganizationDetails: builder.query<OrganizationDetailsResponse, void>({
            query: () => ({
                url: '/organization/details/',
                method: 'GET',
            }),
            providesTags: [{ type: 'Organization', id: 'DETAILS' }],
        }),

        // PUT /organization/details/ - Update organization name
        updateOrganizationDetails: builder.mutation<UpdateOrganizationResponse, UpdateOrganizationRequest>({
            query: (data) => ({
                url: '/organization/details/',
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: [
                { type: 'Organization', id: 'DETAILS' },
                { type: 'Organization', id: 'LIST' },
            ],
        }),

        // GET /organizations/users/ - List users in current organization (org ID from token)
        getOrganizationUsers: builder.query<OrganizationUser[], void>({
            query: () => ({
                url: '/organizations/users/',
                method: 'GET',
            }),
            providesTags: (result) =>
                result
                    ? [
                        ...result.map(({ id }) => ({ type: 'OrganizationUser' as const, id })),
                        { type: 'OrganizationUser', id: 'LIST' },
                    ]
                    : [{ type: 'OrganizationUser', id: 'LIST' }],
        }),

        // POST /organizations/invite/ - Invite user to current organization (org ID from token)
        inviteUser: builder.mutation<SuccessResponse, UserInviteRequest>({
            query: (data) => ({
                url: '/organizations/invite/',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: [
                { type: 'OrganizationUser', id: 'LIST' },
                { type: 'Organization', id: 'DETAILS' }, // Refresh details in case seats change
            ],
        }),

        // PATCH /organizations/users/update/ - Update user role
        updateUserRole: builder.mutation<UpdateUserRoleResponse, UpdateUserRoleRequest>({
            query: (data) => ({
                url: '/organizations/users/update/',
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: [
                { type: 'OrganizationUser', id: 'LIST' },
            ],
        }),

        // DELETE /organizations/users/remove/ - Remove user from organization
        removeUser: builder.mutation<RemoveUserResponse, RemoveUserRequest>({
            query: (data) => ({
                url: '/organizations/users/remove/',
                method: 'DELETE',
                body: {
                    external_user_id: data.external_user_id,
                    external_org_id: data.external_org_id, // ✅ send this
                },
            }),
            invalidatesTags: [
                { type: 'OrganizationUser', id: 'LIST' },
                { type: 'Organization', id: 'DETAILS' }, // Refresh details in case seats change
            ],
        }),

        // POST /organizations/invite/resend/ - Resend invitation
        resendInvite: builder.mutation<ResendInviteResponse, ResendInviteRequest>({
            query: (data) => ({
                url: '/organizations/invite/resend/',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: [
                { type: 'OrganizationUser', id: 'LIST' },
            ],
        }),
    }),
    overrideExisting: false,
});

// Export hooks for usage in components
export const {
    useGetOrganizationsQuery,
    useLazyGetOrganizationsQuery,
    useCreateOrganizationMutation,
    useGetOrganizationDetailsQuery,
    useUpdateOrganizationDetailsMutation,
    useGetOrganizationUsersQuery,
    useLazyGetOrganizationUsersQuery,
    useInviteUserMutation,
    useUpdateUserRoleMutation,
    useRemoveUserMutation,
    useResendInviteMutation,
} = organizationApi;